import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import StorageIcon from "@mui/icons-material/Storage";
import ShieldIcon from "@mui/icons-material/Shield";
import BugReportIcon from "@mui/icons-material/BugReport";
import DescriptionIcon from "@mui/icons-material/Description";

import StepCard from "../../components/evaluation/StepCard";
import {
  fetchEvaluationList,
  startEvaluationPhase,
  validateEvaluationPhase,
  cancelVulnSubScan,
  retryVulnSubScan,
} from "../../redux/actions/evaluationAction";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { getOrganizatonMetricsById } from "../../redux/actions/organizationProfileCreators";

const steps = [
  {
    id: 1,
    title: "Reconnaissance",
    phase: "reconnaissance",
    actionText: "Start",
    icon: <SearchIcon />,
  },
  {
    id: 2,
    title: "Asset Inventory",
    phase: "assets_discovery",
    icon: <StorageIcon />,
  },
  {
    id: 3,
    title: "Risk Identification",
    phase: "risk_scenarios",
    icon: <BugReportIcon />,
  },
  {
    id: 4,
    title: "Risk Assessment",
    phase: "risk_assesment",
    icon: <ShieldIcon />,
  },
  {
    id: 5,
    title: "Report Generation",
    phase: "generate_report",
    icon: <DescriptionIcon />,
  },
];

const MANUAL_VALIDATION_PHASES = new Set([
  "reconnaissance",
  "assets_discovery",
  "risk_scenarios",
  "risk_assesment",
]);

export default function Index() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const storeOrgaId = useSelector((state) => state.organizationProfile?.id);
  const orgaId = storeOrgaId || localStorage.getItem("orgaId");
  const evaluationList = useSelector(
    (state) => state.evaluation?.evaluations ?? []
  );
  const vulnSubScanStatus = useSelector(
    (state) => state.evaluation?.vulnSubScanStatus ?? "not_started"
  );
  const vulnSubScanCancelStatus = useSelector(
    (state) => state.evaluation?.vulnSubScanCancelStatus ?? "not_requested"
  );
  const riskReportStatus = useSelector(
    (state) => state.evaluation?.riskReportStatus ?? "not_started"
  );
  const retryVulnSubScanAllowed = useSelector(
    (state) => Boolean(state.evaluation?.retryVulnSubScanAllowed)
  );
  const vulnSubScanRetryCount = useSelector(
    (state) => Number(state.evaluation?.vulnSubScanRetryCount ?? 0)
  );
  const cancelVulnSubScanAllowed = useSelector(
    (state) => Boolean(state.evaluation?.cancelVulnSubScanAllowed)
  );
  const orgaProfileMetrics =
    useSelector((state) => state.organizationProfile?.organizationMetricsById) ||
    {};
  const globalValidationEnabled =
    typeof orgaProfileMetrics?.validation_status === "boolean"
      ? orgaProfileMetrics.validation_status
      : true;
  const history = useHistory();
  const [riskConfirmOpen, setRiskConfirmOpen] = useState(false);
  const [pendingRiskPhase, setPendingRiskPhase] = useState(null);
  const [validatingPhase, setValidatingPhase] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [retrySubmitting, setRetrySubmitting] = useState(false);

  useEffect(() => {
    if (!orgaId) return;
    dispatch(fetchEvaluationList(orgaId));
  }, [dispatch, orgaId]);

  const normalizeStatus = (rawValue) => {
    const value = String(rawValue ?? "not_started").trim().toLowerCase();
    if (value === "in_progress") {
      return "in_progress";
    }
    if (value === "not_started" ) {
      return "not_started";
    }
    if (value === "failure") {
      return "failed";
    }
    return value;
  };

  const mergedSteps = steps.reduce((acc, s, idx) => {
    const match = evaluationList.find((e) => e.phase === s.phase);
    const status = normalizeStatus(match?.status);
    const raw_user_validated = Boolean(match?.user_validated);
    const validation_status =
      typeof match?.validation_status === "boolean"
        ? match.validation_status
        : globalValidationEnabled;
    const validationRequired =
      validation_status && MANUAL_VALIDATION_PHASES.has(s.phase);
    const user_validated =
      MANUAL_VALIDATION_PHASES.has(s.phase) && !validationRequired
        ? true
        : raw_user_validated;

    const prev = acc[idx - 1];
    let unlocked = idx === 0 ? true : prev?.status === "completed";
    if (s.phase === "risk_assesment") {
      unlocked = status !== "not_started";
    }

    let actionText =
      status === "not_started" && unlocked ? `Start ${s.title}` : undefined;

    if (status === "completed") {
      actionText =
        s.phase === "generate_report"
          ? "View Report"
          : s.phase === "assets_discovery"
          ? "View Assets"
          : s.phase === "risk_assesment"
          ? "View Risk Assessment"
          : s.phase === "risk_scenarios"
          ? user_validated
            ? "View Scenarios"
            : "Select Scenarios"
          : `View ${s.title}`;
    }

    const prevValidated =
      idx === 0
        ? true
        : s.phase === "risk_assesment"
        ? true
        : Boolean(prev?.user_validated);

    const statusLabel =
      status === "completed"
        ? s.phase === "generate_report"
          ? "Completed"
          : validationRequired && user_validated
          ? "Validated"
          : "Completed"
        : status === "in_progress"
        ? "In progress"
        : status === "not_started"
        ? "Waiting to start"
        : status === "failed"
        ? "Failed"
        : status;

    acc.push({
      ...s,
      status,
      statusLabel,
      unlocked,
      actionText,
      user_validated,
      validation_status,
      validationRequired,
      prevValidated,
    });
    return acc;
  }, []);

  const getPhaseDescription = (step, nextStepStatus = "not_started") => {
    if (step.status === "failed") {
      if (step.phase === "risk_scenarios" && retryVulnSubScanAllowed) {
        return "Risk identification has been paused due to a failure in the subdomain vulnerability scan.";
      }
      return `An error occurred.
A support ticket has been opened and our team is investigating.
Thank you for your patience.`;
    }

    if (step.phase === "reconnaissance") {
      if (step.status === "in_progress") {
        return "Cytailor is analyzing your organization's information to establish your profile.";
      }
      if (step.status === "completed" && !step.user_validated) {
        return "Your business function and organization profile are ready. Please review and validate this phase.";
      }
      if (step.status === "completed" && step.user_validated) {
        if (nextStepStatus !== "not_started") {
          return "Organization profile finalized and already reused in the following steps.";
        }
        return "This phase is confirmed. You can now proceed to the Asset Inventory.";
      }
      return "Start the reconnaissance phase to prepare your risk evaluation workflow.";
    }

    if (step.phase === "assets_discovery") {
      if (step.status === "in_progress") {
        return "Cytailor is collecting your external assets.";
      }
      if (step.status === "completed" && !step.user_validated) {
        return "Your external asset list is ready. Please review and validate this phase to continue.";
      }
      if (step.status === "completed" && step.user_validated) {
        if (nextStepStatus !== "not_started") {
          return "External assets are identified and organized, and now feed the following steps.";
        }
        return "This phase is now confirmed. You may proceed to risk identification.";
      }
      return "Thank you for validating the reconnaissance phase. Click Start to begin the asset inventory.";
    }

    if (step.phase === "risk_scenarios") {
      if (step.status === "in_progress") {
        const canUseCancelFlow =
          step.validation_status === true &&
          riskReportStatus === "in_progress" &&
          cancelVulnSubScanAllowed;
        if (
          canUseCancelFlow &&
          vulnSubScanStatus === "in_progress" &&
          vulnSubScanCancelStatus === "in_progress"
        ) {
          return "Cancellation requested. Cytailor is stopping the vulnerability subdomain scan. Partial data may be used for risk identification.";
        }
        if (
          canUseCancelFlow &&
          vulnSubScanStatus === "in_progress" &&
          vulnSubScanCancelStatus === "failed"
        ) {
          return "The previous cancellation request could not be confirmed. You can try canceling the scan again.";
        }
        return "Cytailor is preparing your risk scenario list based on vulnerabilities associated with your assets";
      }
      if (step.status === "completed") {
        if (!step.validationRequired) {
          if (nextStepStatus !== "not_started") {
            return "Risk scenarios are generated and are now used in the following step.";
          }
          return "Your risk scenarios are ready.";
        }
        if (nextStepStatus === "not_started") {
          return "Your risk scenarios are ready. Please select up to 5 scenarios, then choose your report generation method (Generate Quiz or Cytailor Assumptions).";
        }
        if (nextStepStatus === "in_progress") {
          return "Your scenario selection is confirmed. Cytailor will use default assumptions to generate your Risk Report.";
        }
        if (nextStepStatus === "completed") {
          return "Risk scenarios are generated, and your selected scenarios are now used in the following step.";
        }
        return "Your risk scenario selection is confirmed. Continue to generate your Risk Report.";
      }
      return "Click Start to launch the risk identification phase.";
    }

    if (step.phase === "generate_report") {
      if (step.status === "in_progress") {
        return "Your Risk Report is being generated. Please wait.";
      }
      if (step.status === "completed") {
        return "Congratulations! Your Risk Report is now ready.";
      }
      return "You can now generate your Risk Report.";
    }

    if (step.phase === "risk_assesment") {
      if (step.status === "in_progress") {
        return "Cytailor is processing your assumptions and selected scenarios to prepare the final report context.";
      }
      if (step.status === "completed") {
        if (step.user_validated) {
          return "Risk assessment assumptions are validated and ready for report generation.";
        }
        return "Your risk assessment assumptions are ready. Review and validate this phase to continue.";
      }
      return "This phase starts automatically after you validate your scenario selection.";
    }

    return "";
  };

  function onActionClick(step) {
    const canUseEditMode = Boolean(step.validation_status && !step.user_validated);
    if (step.phase === "reconnaissance") {
      history.push({
        pathname: "/orga_assets",
        state: { editMode: canUseEditMode },
      });
    } else if (step.phase === "assets_discovery") {
      history.push({
        pathname: "/asset_inventory",
        state: { editMode: canUseEditMode },
      });
    } else if (step.phase === "risk_scenarios") {
      history.push("/risk-scenarios");
    } else if (step.phase === "risk_assesment") {
      history.push({
        pathname: "/risk-assessment",
        state: { editMode: canUseEditMode },
      });
    } else if (step.phase === "generate_report") {
      history.push("/reports");
    }
  }

  function onOrgaProfileClick(step) {
    history.push({
      pathname: "/profile",
      state: {
        editMode: Boolean(step.validation_status && !step.user_validated),
      },
    });
  }

  async function onContinueClick(stepPhase, extraPayload = {}) {
    try {
      await dispatch(startEvaluationPhase(orgaId, stepPhase, extraPayload));
      await dispatch(fetchEvaluationList(orgaId));
      await dispatch(getOrganizatonMetricsById(orgaId));
    } catch (e) {
      console.error("Failed to continue step and refresh:", e);
    }
  }

  async function onValidateClick(stepPhase) {
    if (!stepPhase || validatingPhase) {
      return;
    }
    setValidatingPhase(stepPhase);
    try {
      await dispatch(validateEvaluationPhase(orgaId, stepPhase));
      await dispatch(fetchEvaluationList(orgaId));
      await dispatch(getOrganizatonMetricsById(orgaId));
    } catch (e) {
      console.error("Failed to validate step and refresh:", e);
    } finally {
      setValidatingPhase(null);
    }
  }

  function openRiskApprovalDialog(phase) {
    setPendingRiskPhase(phase);
    setRiskConfirmOpen(true);
  }

  function closeRiskApprovalDialog() {
    setRiskConfirmOpen(false);
    setPendingRiskPhase(null);
  }

  async function confirmRiskApprovalDialog() {
    const phase = pendingRiskPhase || "risk_scenarios";
    closeRiskApprovalDialog();
    await onContinueClick(phase, { approve_vuln_scan: true });
  }

  function openCancelDialog() {
    setCancelDialogOpen(true);
  }

  function closeCancelDialog() {
    if (!cancelSubmitting) {
      setCancelDialogOpen(false);
    }
  }

  async function confirmCancelDialog() {
    if (cancelSubmitting) return;
    setCancelSubmitting(true);
    try {
      await dispatch(cancelVulnSubScan(orgaId));
      await dispatch(fetchEvaluationList(orgaId));
      await dispatch(getOrganizatonMetricsById(orgaId));
      setCancelDialogOpen(false);
    } catch (e) {
      console.error("Failed to request scan cancellation:", e);
    } finally {
      setCancelSubmitting(false);
    }
  }

  async function onRetryVulnSubScan() {
    if (retrySubmitting) return;
    setRetrySubmitting(true);
    try {
      await dispatch(retryVulnSubScan(orgaId));
      await dispatch(fetchEvaluationList(orgaId));
      await dispatch(getOrganizatonMetricsById(orgaId));
    } catch (e) {
      console.error("Failed to request vuln_sub_scan retry:", e);
    } finally {
      setRetrySubmitting(false);
    }
  }

  function renderRightActions(step) {
    const isLocked = !(step.unlocked && step.prevValidated);
    const canRetryFailedVulnSubScan =
      step.phase === "risk_scenarios" &&
      step.status === "failed" &&
      retryVulnSubScanAllowed;
    if (isLocked || (step.status === "failed" && !canRetryFailedVulnSubScan)) {
      return null;
    }

    const outlinedBtnSx = {
      borderRadius: 999,
      borderColor: alpha(theme.palette.primary.main, 0.2),
      color: theme.palette.text.primary,
      backgroundColor: alpha(theme.palette.background.paper, 0.92),
      textTransform: "none",
      minHeight: { xs: 40, sm: 34 },
      fontSize: { xs: "0.9rem", sm: "0.86rem" },
      fontWeight: 500,
      px: { xs: 1.6, sm: 2.1 },
      width: { xs: "100%", sm: "auto" },
      lineHeight: 1.15,
      textAlign: "center",
    };

    const primaryBtnSx = {
      borderRadius: 999,
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText || "#fff",
      "&:hover": { backgroundColor: theme.palette.success.dark },
      textTransform: "none",
      minHeight: { xs: 40, sm: 34 },
      fontSize: { xs: "0.9rem", sm: "0.86rem" },
      fontWeight: 600,
      px: { xs: 1.6, sm: 2.1 },
      width: { xs: "100%", sm: "auto" },
      lineHeight: 1.15,
      textAlign: "center",
    };

    const isValidateLoading = validatingPhase === step.phase;

    if (canRetryFailedVulnSubScan) {
      return (
        <Box
          sx={{
            mt: { xs: 1.25, sm: 1.15 },
            width: "100%",
            display: "flex",
            justifyContent: { xs: "stretch", sm: "flex-end" },
          }}
        >
          <Button
            variant="contained"
            sx={{
              ...primaryBtnSx,
              minWidth: { xs: "100%", sm: 118 },
              minHeight: { xs: 38, sm: 30 },
              fontSize: { xs: "0.88rem", sm: "0.82rem" },
              px: { xs: 1.4, sm: 1.8 },
            }}
            disabled={retrySubmitting}
            startIcon={
              retrySubmitting ? <CircularProgress size={16} color="inherit" /> : undefined
            }
            onClick={onRetryVulnSubScan}
          >
            Retry
          </Button>
        </Box>
      );
    }

    if (step.status === "in_progress") {
      const canShowCancel =
        step.phase === "risk_scenarios" &&
        vulnSubScanStatus === "in_progress" &&
        step.validation_status === true &&
        riskReportStatus === "in_progress" &&
        cancelVulnSubScanAllowed;
      if (!canShowCancel) {
        return null;
      }

      const canceling = vulnSubScanCancelStatus === "in_progress";

      return (
        <Box
          sx={{
            mt: { xs: 1.25, sm: 1.15 },
            width: "100%",
            display: "flex",
            justifyContent: { xs: "stretch", sm: "flex-end" },
          }}
        >
          <Button
            variant="outlined"
            color={canceling ? "inherit" : "error"}
            disabled={canceling}
            startIcon={
              canceling ? <CircularProgress size={16} color="inherit" /> : undefined
            }
            sx={{
              borderRadius: 999,
              textTransform: "none",
              minHeight: { xs: 40, sm: 34 },
              fontSize: { xs: "0.9rem", sm: "0.86rem" },
              fontWeight: 600,
              px: { xs: 1.6, sm: 2.1 },
              width: { xs: "100%", sm: "auto" },
              lineHeight: 1.15,
              textAlign: "center",
              minWidth: { xs: "100%", sm: 170 },
            }}
            onClick={openCancelDialog}
          >
            {canceling ? "Canceling scan..." : "Cancel scan"}
          </Button>
        </Box>
      );
    }

    if (step.status === "not_started") {
      const manualStartPhases = [
        "reconnaissance",
        "assets_discovery",
        "risk_scenarios",
        "generate_report",
      ];
      if (!manualStartPhases.includes(step.phase)) {
        return null;
      }

      const handleStart = () => {
        if (step.phase === "risk_scenarios") {
          openRiskApprovalDialog(step.phase);
          return;
        }
        onContinueClick(step.phase);
      };

      return (
        <Box
          sx={{
            mt: { xs: 1.25, sm: 1.15 },
            width: "100%",
            display: "flex",
            justifyContent: { xs: "stretch", sm: "flex-end" },
          }}
        >
          <Button
            variant="contained"
            sx={{ ...primaryBtnSx, minWidth: { xs: "100%", sm: 176 } }}
            onClick={handleStart}
          >
            {step.phase === "generate_report"
              ? "Generate Report"
              : `Start ${step.title}`}
          </Button>
        </Box>
      );
    }

    if (step.status !== "completed") {
      return null;
    }

    if (step.phase === "reconnaissance") {
      return (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 0.9, sm: 1 }}
          sx={{
            mt: { xs: 1.25, sm: 1.15 },
            width: "100%",
            justifyContent: { xs: "stretch", sm: "flex-end" },
            flexWrap: { xs: "nowrap", sm: "wrap" },
            rowGap: { xs: 0, sm: 0.8 },
          }}
        >
          <Button
            variant="outlined"
            sx={{ ...outlinedBtnSx, minWidth: { xs: "100%", sm: 190 } }}
            onClick={() => onActionClick(step)}
          >
            View Business Functions
          </Button>
          <Button
            variant="outlined"
            sx={{ ...outlinedBtnSx, minWidth: { xs: "100%", sm: 196 } }}
            onClick={() => onOrgaProfileClick(step)}
          >
            View Organization Profile
          </Button>
          {step.validationRequired && !step.user_validated && (
            <Button
              variant="contained"
              sx={{ ...primaryBtnSx, minWidth: { xs: "100%", sm: 138 } }}
              disabled={isValidateLoading || Boolean(validatingPhase)}
              startIcon={
                isValidateLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
              onClick={() => onValidateClick(step.phase)}
            >
              {isValidateLoading ? "Validating..." : "Validate"}
            </Button>
          )}
        </Stack>
      );
    }

    if (step.phase === "risk_assesment") {
      return (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 0.9, sm: 1 }}
          sx={{
            mt: { xs: 1.25, sm: 1.15 },
            width: "100%",
            justifyContent: { xs: "stretch", sm: "flex-end" },
            flexWrap: { xs: "nowrap", sm: "wrap" },
            rowGap: { xs: 0, sm: 0.8 },
          }}
        >
          <Button
            variant="outlined"
            sx={{ ...outlinedBtnSx, minWidth: { xs: "100%", sm: 190 } }}
            onClick={() => onActionClick(step)}
          >
            View Risk Assessment
          </Button>
          {step.validationRequired && !step.user_validated && (
            <Button
              variant="contained"
              sx={{ ...primaryBtnSx, minWidth: { xs: "100%", sm: 138 } }}
              disabled={isValidateLoading || Boolean(validatingPhase)}
              startIcon={
                isValidateLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
              onClick={() => onValidateClick(step.phase)}
            >
              {isValidateLoading ? "Validating..." : "Validate"}
            </Button>
          )}
        </Stack>
      );
    }

    const viewLabel =
      step.phase === "generate_report"
        ? "View Report"
      : step.phase === "assets_discovery"
        ? "View Assets"
      : step.phase === "risk_assesment"
        ? "View Risk Assessment"
      : step.phase === "risk_scenarios"
        ? step.user_validated
          ? "View Scenarios"
          : "Select Scenarios"
        : `View ${step.title}`;
    const isRiskScenariosSelectCta =
      step.phase === "risk_scenarios" && !step.user_validated;

    return (
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 0.9, sm: 1 }}
        sx={{
          mt: { xs: 1.25, sm: 1.15 },
          width: "100%",
          justifyContent: { xs: "stretch", sm: "flex-end" },
          flexWrap: { xs: "nowrap", sm: "wrap" },
          rowGap: { xs: 0, sm: 0.8 },
        }}
      >
        <Button
          variant={isRiskScenariosSelectCta ? "contained" : "outlined"}
          sx={
            isRiskScenariosSelectCta
              ? { ...primaryBtnSx, minWidth: { xs: "100%", sm: 176 } }
              : { ...outlinedBtnSx, minWidth: { xs: "100%", sm: 176 } }
          }
          onClick={() => onActionClick(step)}
        >
          {viewLabel}
        </Button>
        {step.validationRequired &&
          !step.user_validated &&
          step.phase !== "risk_scenarios" && (
          <Button
            variant="contained"
            sx={{ ...primaryBtnSx, minWidth: { xs: "100%", sm: 138 } }}
            disabled={isValidateLoading || Boolean(validatingPhase)}
            startIcon={
              isValidateLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            onClick={() => onValidateClick(step.phase)}
          >
            {isValidateLoading ? "Validating..." : "Validate"}
          </Button>
        )}
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        px: { xs: 1.15, sm: 1.5, md: 2.2 },
        py: 2,
        marginBottom: "20px",
        position: "relative",
      }}
    >
      <Stack
        spacing={{ xs: 1.7, md: 1.8 }}
        sx={{
          width: "100%",
          maxWidth: { lg: 1160 },
          mx: 0,
          position: "relative",
        }}
      >
        {mergedSteps.map((step) => (
          (() => {
            const isLocked = !(step.unlocked && step.prevValidated);
            const showDescription = !isLocked;
            const stepIndex = mergedSteps.findIndex((s) => s.id === step.id);
            const nextStepStatus = mergedSteps[stepIndex + 1]?.status ?? "not_started";

            return (
                <Box
                  key={step.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      lg: showDescription ? "286px minmax(340px, 1fr)" : "286px",
                    },
                    gap: { xs: 1.25, md: 1.8 },
                    alignItems: "center",
                  }}
                >
                <Box sx={{ width: "100%", maxWidth: { xs: "100%", lg: 286 } }}>
                  <StepCard
                    {...step}
                    user_validated={Boolean(step.user_validated)}
                  />
                </Box>

                {showDescription && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      minHeight: { xs: "auto", md: 108 },
                      px: { xs: 1.4, md: 1.8 },
                      py: { xs: 1.15, md: 1.05 },
                      borderRadius: { xs: 2.5, md: 3 },
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                      backgroundColor: alpha(theme.palette.background.paper, 0.98),
                      boxShadow: `0 6px 14px ${alpha(theme.palette.primary.main, 0.05)}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.primary,
                          lineHeight: 1.52,
                          whiteSpace: "pre-line",
                          fontSize: { xs: "0.92rem", md: "0.93rem" },
                          fontWeight: { xs: 500, md: 600 },
                          opacity: 1,
                          textAlign: { xs: "left", md: "center" },
                        }}
                      >
                        {getPhaseDescription(step, nextStepStatus)}
                      </Typography>
                      {renderRightActions(step)}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })()
        ))}
      </Stack>

      <Dialog open={riskConfirmOpen} onClose={closeRiskApprovalDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Approve External Vulnerability Scan</DialogTitle>
        <DialogContent>
          <DialogContentText>
            I declare and guarantee that I hold the Active Scan Authorization 
            signed by the legal representative of the domain owner, in compliance with applicable regulations.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRiskApprovalDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={confirmRiskApprovalDialog} variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={closeCancelDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Cancel Vulnerability Subdomain Scan ?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: theme.palette.text.primary }}>
            Stopping the scan now will interrupt the ongoing subdomain vulnerability analysis.
            As a result, some findings may be incomplete or missing, which could impact risk identification accuracy
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-end", gap: 1.25 }}>
          <Button
            onClick={confirmCancelDialog}
            color="error"
            variant="outlined"
            disabled={cancelSubmitting}
            startIcon={cancelSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {cancelSubmitting ? "Requesting..." : "Confirm cancel"}
          </Button>
          <Button
            onClick={closeCancelDialog}
            color="success"
            variant="contained"
            disabled={cancelSubmitting}
            autoFocus
          >
            Keep Scan Running
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
